// src/components/ServiceMarkerListOrder.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

const ServiceMarkerListOrder = () => {
  const { orderId } = useParams(); // Hole die orderId aus den URL-Parametern
  const navigate = useNavigate();

  // Zustände für ServiceMarkers, OrderItems und den Auftrag
  const [serviceMarkers, setServiceMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newMarker, setNewMarker] = useState({
    orderItemId: '',
    UG: '',
    RN: '',
    operationLocationUG: '',
    operationLocationRN: '',
    serviceNotes: '',
    UGTime: '',
    RNTime: '',
  });
  const [orderItems, setOrderItems] = useState([]);
  const [order, setOrder] = useState(null);

  // Zustände für Auto-Erstellung
  const [autoCreated, setAutoCreated] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false);

  // Zusätzliche Zustände für Bearbeitung
  const [editMarkerId, setEditMarkerId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    orderItemId: '',
    UG: '',
    RN: '',
    operationLocationUG: '',
    operationLocationRN: '',
    serviceNotes: '',
    UGTime: '',
    RNTime: '',
  });

  // Lade Order-Daten, OrderItems und ServiceMarkers in Reihenfolge
  useEffect(() => {
    const fetchData = async () => {
      await fetchOrder();          // Zuerst Auftragsdaten laden
      await fetchOrderItems();     // Dann OrderItems laden
      await fetchServiceMarkers(); // Anschließend ServiceMarkers laden
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Funktion zum Abrufen der Order-Daten
  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/details`);
      setOrder(response.data);
      console.log(`Order-Daten für Auftrag ${orderId}:`, response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen des Auftrags:', error);
      alert('Fehler beim Laden der Auftragsdaten.');
    }
  };

  // Funktion zum Abrufen aller OrderItems für den Auftrag
  const fetchOrderItems = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/orderitems`);
      const filteredItems = response.data.filter(
        (item) => item.item && item.item.itemType === 'rental' && item.item.visibleInCalendar
      );
      setOrderItems(filteredItems);
    } catch (error) {
      console.error('Fehler beim Abrufen der OrderItems:', error);
      alert('Fehler beim Laden der OrderItems.');
    }
  };

  // Funktion zum Abrufen aller ServiceMarkers für den Auftrag
  const fetchServiceMarkers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/servicemarkers/byOrder/${orderId}?t=${new Date().getTime()}`);
      setServiceMarkers(response.data);
      console.log(`ServiceMarkers für Auftrag ${orderId}:`, response.data);

      // Prüfen, ob bereits ServiceMarkers automatisch erstellt wurden
      const rentalOrderItems = orderItems.filter(item => item.item && item.item.itemType === 'rental');
      const autoCreatedMarkers = response.data.filter(marker =>
        rentalOrderItems.some(item => item.id === marker.orderItemId)
      );

      if (autoCreatedMarkers.length > 0) {
        setAutoCreated(true);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der ServiceMarkers:', error);
      if (error.response && error.response.status === 404) {
        alert('Keine ServiceMarkers für diesen Auftrag gefunden.');
      } else {
        alert('Fehler beim Laden der ServiceMarkers.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion zum Abrufen eines einzelnen ServiceMarkers
  const fetchServiceMarkerById = async (id) => {
    try {
      const response = await api.get(`/servicemarkers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Abrufen des ServiceMarkers:', error);
      alert('Fehler beim Laden des ServiceMarkers.');
      return null;
    }
  };

  // Handler zum Öffnen des Bearbeitungsformulars
  const handleEditMarker = async (markerId) => {
    const markerData = await fetchServiceMarkerById(markerId);
    if (markerData) {
      setEditMarkerId(markerId);
      setEditFormData({
        orderItemId: markerData.orderItemId || '',
        UG: markerData.UG ? markerData.UG.slice(0, 10) : '',
        RN: markerData.RN ? markerData.RN.slice(0, 10) : '',
        operationLocationUG: markerData.operationLocationUG || '',
        operationLocationRN: markerData.operationLocationRN || '',
        serviceNotes: markerData.serviceNotes || '',
        UGTime: markerData.UGTime || '',
        RNTime: markerData.RNTime || '',
      });
      setShowForm(true);
    }
  };

  // Handler für Änderungen im Bearbeitungsformular
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler für Änderungen im Erstellungsformular
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMarker((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler zum Absenden des Bearbeitungsformulars
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editFormData.orderItemId) {
        alert('Bitte ein OrderItem auswählen.');
        return;
      }

      const payload = {
        orderItemId: parseInt(editFormData.orderItemId, 10),
        lwOperatorId: order?.lwOperator?.id ?? null,
        UG: editFormData.UG ? new Date(editFormData.UG).toISOString() : null,
        RN: editFormData.RN ? new Date(editFormData.RN).toISOString() : null,
        operationLocationUG: editFormData.operationLocationUG,
        operationLocationRN: editFormData.operationLocationRN,
        serviceNotes: editFormData.serviceNotes,
        UGTime: editFormData.UGTime,
        RNTime: editFormData.RNTime,
      };

      const response = await api.put(`/servicemarkers/${editMarkerId}`, payload);
      console.log('ServiceMarker aktualisiert:', response.data);
      alert('ServiceMarker erfolgreich aktualisiert!');
      setShowForm(false);
      setEditMarkerId(null);
      fetchServiceMarkers(); // Aktualisiere die Liste
    } catch (error) {
      console.error('Fehler beim Aktualisieren des ServiceMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Aktualisieren des ServiceMarkers.');
      }
    }
  };

  // Handler zum Erstellen eines neuen ServiceMarkers
  const handleCreateMarker = async (e) => {
    e.preventDefault();
    try {
      if (!newMarker.orderItemId) {
        alert('Bitte ein OrderItem auswählen.');
        return;
      }

      const payload = {
        orderItemId: parseInt(newMarker.orderItemId, 10),
        lwOperatorId: order?.lwOperator?.id ?? null,
        UG: newMarker.UG ? new Date(newMarker.UG).toISOString() : null,
        RN: newMarker.RN ? new Date(newMarker.RN).toISOString() : null,
        operationLocationUG: newMarker.operationLocationUG,
        operationLocationRN: newMarker.operationLocationRN,
        serviceNotes: newMarker.serviceNotes,
        UGTime: newMarker.UGTime,
        RNTime: newMarker.RNTime,
      };

      const response = await api.post('/servicemarkers', payload);
      console.log('Neuer ServiceMarker erstellt:', response.data);
      alert('ServiceMarker erfolgreich erstellt!');
      setShowForm(false);
      fetchServiceMarkers();
    } catch (error) {
      console.error('Fehler beim Erstellen des ServiceMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Erstellen des ServiceMarkers.');
      }
    }
  };

  // Handler zum Update aller ServiceMarkers
  const handleBulkUpdateMarkers = async () => {
    if (!window.confirm('Möchten Sie alle ServiceMarkers dieses Auftrags aktualisieren?')) {
      return;
    }
  
    try {
      const response = await api.put(`/servicemarkers/byOrder/${orderId}/updateAll`);
      console.log('ServiceMarkers aktualisiert:', response.data);
      alert(response.data.message);
      fetchServiceMarkers();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der ServiceMarkers:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Aktualisieren der ServiceMarkers.');
      }
    }
  };

  // Handler zum Löschen eines ServiceMarkers
  const handleDeleteMarker = async (markerId) => {
    if (!window.confirm(`Möchtest du den ServiceMarker #${markerId} wirklich löschen?`)) return;
  
    try {
      await api.delete(`/servicemarkers/${markerId}`);
      alert('ServiceMarker erfolgreich gelöscht!');
      setServiceMarkers((prevMarkers) => prevMarkers.filter(marker => marker.id !== markerId));
    } catch (error) {
      console.error('Fehler beim Löschen des ServiceMarkers:', error);
      alert('Fehler beim Löschen des ServiceMarkers.');
    }
  };

  // Handler zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setShowForm(false);
    setEditMarkerId(null);
  };

  // Handler zur automatischen Erstellung der ServiceMarkers
  const handleAutoCreateMarkers = async () => {
    if (autoCreated) {
      alert("ServiceMarkers wurden bereits automatisch erstellt.");
      return;
    }

    if (!order) {
      alert("Auftragsdaten sind nicht geladen.");
      return;
    }

    const rentalOrderItems = orderItems.filter(item => item.item && item.item.itemType === 'rental');

    if (rentalOrderItems.length === 0) {
      alert("Keine OrderItems mit dem Typ 'rental' gefunden.");
      return;
    }

    if (!window.confirm(`Möchtest du ServiceMarkers für ${rentalOrderItems.length} OrderItems erstellen?`)) {
      return;
    }

    setIsAutoCreating(true);

    try {
      const payload = {
        orderId: parseInt(orderId, 10),
      };

      const response = await api.post('/servicemarkers/bulk', payload);
      console.log("ServiceMarkers erstellt:", response.data);
      alert(response.data.message);
      fetchServiceMarkers();
      setAutoCreated(true);
    } catch (error) {
      console.error("Fehler beim automatischen Erstellen der ServiceMarkers:", error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert("Fehler beim Erstellen der ServiceMarkers.");
      }
    } finally {
      setIsAutoCreating(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <Helmet>
        <title>ServiceMarker bearbeiten</title>
        <meta name="description" content="ServiceMarker bearbeiten" />
      </Helmet>
      <h2>ServiceMarker für Auftrag #{orderId}, {order?.orderNumber || 'Keine Auftragsnummer vorhanden'}</h2>

      {/* Buttons */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => navigate(`/orders/${orderId}/edit`)}
          style={{ marginBottom: '20px', padding: '5px 10px', cursor: 'pointer' }}
        >
          Zurück zum Auftrag
        </button>

        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setEditMarkerId(null);
            setNewMarker({
              orderItemId: '',
              UG: '',
              RN: '',
              operationLocationUG: order ? order.operationLocation : '',
              operationLocationRN: order ? order.operationLocation : '',
              serviceNotes: order ? order.notes : '',
              UGTime: '',
              RNTime: '',
            });
          }}
          style={{
            marginBottom: '20px',
            padding: '5px 10px',
            backgroundColor: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
        >
          Neuen ServiceMarker erstellen
        </button>

        <button
          type="button"
          onClick={handleAutoCreateMarkers}
          disabled={autoCreated || isAutoCreating || !order}
          style={{
            marginBottom: '20px',
            padding: '5px 10px',
            backgroundColor: '#17a2b8',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: autoCreated || isAutoCreating || !order ? 'not-allowed' : 'pointer',
            marginLeft: '10px',
          }}
        >
          {isAutoCreating ? 'Erstelle ServiceMarkers...' : 'ServiceMarkers automatisch erstellen'}
        </button>

        <button
          type="button"
          onClick={handleBulkUpdateMarkers}
          style={{
            marginBottom: '20px',
            padding: '5px 10px',
            backgroundColor: '#ffc107',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
        >
          Alle ServiceMarkers aktualisieren
        </button>
      </div>

      {/* ServiceMarker-Liste */}
      {isLoading ? (
        <p>Lade ServiceMarker...</p>
      ) : serviceMarkers.length === 0 ? (
        <p>Keine ServiceMarkers für diesen Auftrag vorhanden.</p>
      ) : (
        <table border="1" cellPadding="6" style={{ marginTop: '1rem', width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Order Number</th>
              <th>Customer Company</th>
              <th>OrderItem Name</th>
              <th>LW Operator</th>
              <th>UG</th>
              <th>UG Time</th>
              <th>RN</th>
              <th>RN Time</th>
              <th>Op. Location UG</th>
              <th>Op. Location RN</th>
              <th>Service Notes</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {serviceMarkers.map((marker) => {
              const associatedOrderItem = orderItems.find(item => item.id === marker.orderItemId);
              const orderNumber = order ? order.orderNumber : '–';
              const customerCompany = order && order.customer ? order.customer.company : '–';
              const lwOperator = order && order.lwOperator ? `${order.lwOperator.contactFirstName} ${order.lwOperator.contactLastName}` : '–';
              const itemName = associatedOrderItem && associatedOrderItem.item ? associatedOrderItem.item.name : '–';

              return (
                <tr key={marker.id}>
                  <td>{marker.id}</td>
                  <td>{orderNumber}</td>
                  <td>{customerCompany}</td>
                  <td>{itemName}</td>
                  <td>{lwOperator}</td>
                  <td>
                    {marker.UG
                      ? new Date(marker.UG).toLocaleDateString('de-DE', {
                          timeZone: 'Europe/Berlin',
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric',
                        })
                      : '–'}
                  </td>
                  <td>{marker.UGTime || '–'}</td>
                  <td>
                    {marker.RN
                      ? new Date(marker.RN).toLocaleDateString('de-DE', {
                          timeZone: 'Europe/Berlin',
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric',
                        })
                      : '–'}
                  </td>
                  <td>{marker.RNTime || '–'}</td>
                  <td>{marker.operationLocationUG || '–'}</td>
                  <td>{marker.operationLocationRN || '–'}</td>
                  <td>{marker.serviceNotes || '–'}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleEditMarker(marker.id)}
                      style={{
                        padding: '3px 6px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        marginRight: '5px',
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteMarker(marker.id)}
                      style={{
                        padding: '3px 6px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Formular zum Erstellen oder Bearbeiten eines ServiceMarkers */}
      {showForm && (
        <div style={{ marginTop: '20px', border: '1px solid #ccc', padding: '15px', borderRadius: '5px' }}>
          <h3>{editMarkerId ? `ServiceMarker #${editMarkerId} bearbeiten` : 'Neuen ServiceMarker erstellen'}</h3>
          {orderItems.filter(orderItem => orderItem.item && orderItem.item.itemType === 'rental').length === 0 ? (
            <p>Keine OrderItems mit dem Typ "rental" verfügbar. Bitte erstelle zuerst ein entsprechendes OrderItem.</p>
          ) : (
            <form onSubmit={editMarkerId ? handleEditFormSubmit : handleCreateMarker}>
              {/* OrderItem Auswahl */}
              <label htmlFor="orderItemId" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '465px' }}>
                OrderItem:
                <select
                  id="orderItemId"
                  name="orderItemId"
                  value={editMarkerId ? editFormData.orderItemId : newMarker.orderItemId}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  required
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                >
                  <option value="">-- Bitte wählen --</option>
                  {orderItems
                    .filter(orderItem => orderItem.item && orderItem.item.itemType === 'rental')
                    .map(orderItem => (
                      <option key={orderItem.id} value={orderItem.id}>
                        {orderItem.item.name} (#{orderItem.id})
                      </option>
                  ))}
                </select>
              </label>

              {/* UG Datum */}
              <label htmlFor="UG" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px', marginLeft: '480px' }}>
                UG:
                <input
                  type="date"
                  id="UG"
                  name="UG"
                  value={editMarkerId ? editFormData.UG : newMarker.UG}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

               {/* UG Time */}
               <label htmlFor="UGTime" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px', marginLeft: '480px', marginTop: '10px' }}>
                UG Time:
                <input
                  type="time"
                  id="UGTime"
                  name="UGTime"
                  value={editMarkerId ? editFormData.UGTime : newMarker.UGTime}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* RN Datum */}
              <label htmlFor="RN" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px', marginLeft: '490px' }}>
                RN:
                <input
                  type="date"
                  id="RN"
                  name="RN"
                  value={editMarkerId ? editFormData.RN : newMarker.RN}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

               {/* RN Time */}
               <label htmlFor="RNTime" style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '200px', marginLeft: '490px', marginTop: '10px' }}>
                RN Time:
                <input
                  type="time"
                  id="RNTime"
                  name="RNTime"
                  value={editMarkerId ? editFormData.RNTime : newMarker.RNTime}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Operation Location UG */}
              <label
                htmlFor="operationLocationUG"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '458px', marginTop: '10px' }}
              >
                Ort UG:
                <input
                  type="text"
                  id="operationLocationUG"
                  name="operationLocationUG"
                  value={editMarkerId ? editFormData.operationLocationUG : newMarker.operationLocationUG}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Operation Location RN */}
              <label
                htmlFor="operationLocationRN"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '467px', marginTop: '10px' }}
              >
                Ort RN:
                <input
                  type="text"
                  id="operationLocationRN"
                  name="operationLocationRN"
                  value={editMarkerId ? editFormData.operationLocationRN : newMarker.operationLocationRN}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%' }}
                />
              </label>

              {/* Service Notes */}
              <label
                htmlFor="serviceNotes"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', maxWidth: '400px', marginLeft: '460px', marginTop: '10px' }}
              >
                Service Notes:
                <textarea
                  id="serviceNotes"
                  name="serviceNotes"
                  value={editMarkerId ? editFormData.serviceNotes : newMarker.serviceNotes}
                  onChange={editMarkerId ? handleEditFormChange : handleChange}
                  style={{ marginLeft: '10px', padding: '10px', width: '100%', height: '80px' }}
                />
              </label>

              {/* Formular-Buttons */}
              <div style={{ marginTop: '15px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '5px 10px',
                    backgroundColor: editMarkerId ? '#007bff' : '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    marginRight: '10px',
                  }}
                >
                  {editMarkerId ? 'Aktualisieren' : 'Erstellen'}
                </button>
                {editMarkerId ? (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Abbrechen
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceMarkerListOrder;
